import { IDocument } from '../../Interfaces/IDocument';
import { IInvoiceFullObject } from '../../Interfaces/IInvoice';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalRole } from '../../Interfaces/ILegalRole';
import {
  ILegalTransaction,
  ILegalTransactionFullObject,
} from '../../Interfaces/ILegalTransaction';
import { scanInvoicePdf } from '../../Invoice/clientScan';
import { IDocumentWithScan } from '../../hooks/reactQuerys/InvoiceQueries';

interface Params {
  uploadArray: IDocument[];
  legalClients: ILegalClient[];
  legalRoles: ILegalRole[];
  legalTransaction: ILegalTransaction | ILegalTransactionFullObject;
  documentArray: IDocument[];
  invoiceObjectArray: IInvoiceFullObject[];
  setDocumentArray: (docs: IDocument[]) => void;
  setInvoiceObjectArray: (invoices: IInvoiceFullObject[]) => void;
  setLegalClients: (clients: ILegalClient[]) => void;
  checkDocumentForInvoice: (
    docs: IDocumentWithScan[]
  ) => Promise<IInvoiceFullObject[]>;
  /** Optional: meldet, welche Datei gerade gescannt wird, oder `null`
   *  wenn alle Scans fertig sind. */
  onScanProgress?: (currentFileName: string | null) => void;
}

// Decodiert Base64 zurueck in ein ArrayBuffer fuer pdf.js. Wir nutzen
// keinen Blob+arrayBuffer-Umweg, weil das hier ohnehin synchron in einer
// Schleife laeuft.
function base64ToArrayBuffer(b64: string): ArrayBuffer {
  // Manche Stellen liefern bereits "data:...;base64,xxx", andere nur den
  // Base64-Teil — wir trennen vorsichtshalber ab.
  const idx = b64.indexOf(',');
  const pure = idx >= 0 ? b64.slice(idx + 1) : b64;
  const binary = atob(pure);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

async function attachClientScans(
  docs: IDocument[],
  onProgress?: (currentFileName: string | null) => void
): Promise<IDocumentWithScan[]> {
  // PDF-Dokumente (idMIMEType === 1) bekommen einen Client-Scan. Andere
  // MIMEs (Word/Excel/Bild) lassen wir unveraendert ans Backend gehen.
  const out: IDocumentWithScan[] = [];
  for (const doc of docs) {
    const next: IDocumentWithScan = { ...doc };
    if (doc.idMIMEType === 1 && doc.Data) {
      onProgress?.(doc.FileName ?? null);
      try {
        const buffer = base64ToArrayBuffer(String(doc.Data));
        const result = await scanInvoicePdf(buffer);
        // ClientScan immer setzen, wenn entweder QR ODER Text-Layer
        // brauchbare Felder geliefert hat. Backend nutzt InvoiceNumber
        // fuer das RecordNumber-Matching, EPCString fuer Anzeige/Speicher.
        const hasQr = !!result.qrPayload;
        const hasText = !!(
          result.combined.InvoiceNumber ||
          result.combined.Total ||
          result.combined.IBAN
        );
        if (hasQr || hasText) {
          next.ClientScan = {
            EPCString: result.combined.EPCString,
            Total: result.combined.Total,
            InvoiceNumber: result.combined.InvoiceNumber,
            IBAN: result.combined.IBAN || undefined,
            BIC: result.combined.BIC || undefined,
            Recipient: result.combined.Recipient || undefined,
          };
        }
      } catch (e) {
        console.warn('[Invoice] Client-Scan fehlgeschlagen:', e);
      }
    }
    out.push(next);
  }
  onProgress?.(null);
  return out;
}

export const checkDocumentArrayUtil = async ({
  uploadArray,
  legalClients,
  legalRoles,
  legalTransaction,
  documentArray,
  invoiceObjectArray,
  setDocumentArray,
  setInvoiceObjectArray,
  setLegalClients,
  checkDocumentForInvoice,
  onScanProgress,
}: Params): Promise<void> => {
  const assignLegalClients: number[] = [];

  if (legalClients.length === 1) {
    assignLegalClients.push(legalClients[0].idLegalClient);
  } else {
    legalRoles
      .filter(role => role.isPayer)
      .forEach(role => {
        legalClients
          .filter(client => client.idLegalRole === role.idLegalRole)
          .forEach(client => assignLegalClients.push(client.idLegalClient));
      });
  }

  const scannedUploadArray = await attachClientScans(
    uploadArray,
    onScanProgress
  );
  const checkedInvoice = await checkDocumentForInvoice(scannedUploadArray);
  const forDocumentArray: IDocument[] = [];
  const forInvoiceArray: IInvoiceFullObject[] = [];

  let minIdDocument = -1;
  let minIdInvoice = -1;

  if (documentArray.length > 0) {
    const minTmpId = Math.min(...documentArray.map(x => x.idDocument)) - 1;
    if (minTmpId <= minIdDocument) {
      minIdDocument = minTmpId - 1;
    }
  }

  if (invoiceObjectArray.length > 0) {
    const minTmpId = Math.min(...invoiceObjectArray.map(x => x.idDocument)) - 1;
    if (minTmpId <= minIdInvoice) {
      minIdInvoice = minTmpId - 1;
    }
  }

  checkedInvoice.forEach((invoice, idx) => {
    if (invoice.Total === null) {
      forDocumentArray.push({
        idDocument: minIdDocument - idx,
        Created_at: new Date().toISOString(),
        FileName: invoice.FileName,
        idDocumentDirectory: null,
        idLegalTransaction: legalTransaction.idLegalTransaction,
        idMIMEType: invoice.idMIMEType,
        Data: invoice.Data,
      } as IDocument);
    } else {
      forInvoiceArray.push({
        ...invoice,
        idDocument: minIdInvoice - idx,
        idInvoice: minIdInvoice - idx,
        idLegalTransactionType: legalTransaction.idLegalTransactionType,
        LegalClients: assignLegalClients,
      });
    }
  });

  setDocumentArray([...documentArray, ...forDocumentArray]);
  setInvoiceObjectArray([...invoiceObjectArray, ...forInvoiceArray]);
  setLegalClients(
    legalClients.map(client => ({
      ...client,
      enableInvoiceReminding: assignLegalClients.includes(client.idLegalClient),
    }))
  );
};
